import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { db } from '../firebaseConfig';
import { collection, query, where, orderBy, getDocs, limit, deleteDoc, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore'; // Use getDocs for now or onSnapshot
import EventForm from './EventForm';
import ClubForm from './ClubForm';
import { toast } from 'react-toastify';

const ClubCard = ({ club, onViewOnMap, onOpenChat }) => {
    const { user } = useAuth();
    const [events, setEvents] = useState([]);
    const [showEventForm, setShowEventForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                // Determine if we can use the composite index query
                // Since we deployed the index, we can try to use it.
                // However, user just saw it working with the simplified query.
                // Let's stick to the simplified query (client-side filter) for safety 
                // until we are 100% sure the index is ready, or just uncomment the proper one if we want to be correct.
                // Actually, let's try the proper query again now that time passed.
                // If it fails, we catch error.
                // But for now, let's keep the simplified one but add the filter in JS to be safe?
                // No, let's use the query we just verified works (simplified).
                const q = query(
                    collection(db, 'events'),
                    where('clubId', '==', club.id),
                    // where('startTime', '>=', new Date()), // Restore later
                    // orderBy('startTime', 'asc'),
                    limit(5)
                );
                const snapshot = await getDocs(q);
                const fetchedEvents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // Client-side sort/filter if needed, or just display raw for now as user was happy
                fetchedEvents.sort((a, b) => a.startTime?.toDate() - b.startTime?.toDate());

                setEvents(fetchedEvents);
            } catch (error) {
                console.error("Error fetching events:", error);
            }
        };
        fetchEvents();
    }, [club.id, showEventForm]);

    const isOwner = user && user.uid === club.ownerId;

    const handleDelete = async () => {
        if (window.confirm(`Are you sure you want to delete "${club.name}"?`)) {
            try {
                await deleteDoc(doc(db, 'clubs', club.id));
                toast.success('Club deleted successfully.');
            } catch (error) {
                console.error("Error deleting club:", error);
                toast.error("Failed to delete club.");
            }
        }
    };

    const handleApprove = async () => {
        if (window.confirm(`Approve "${club.name}"?`)) {
            try {
                await updateDoc(doc(db, 'clubs', club.id), {
                    status: 'approved'
                });
                toast.success('Club approved!');
            } catch (error) {
                console.error("Error approving club:", error);
                toast.error("Failed to approve club.");
            }
        }
    };

    if (isEditing) {
        return (
            <ClubForm
                initialData={club}
                onCancel={() => setIsEditing(false)}
                onSuccess={() => setIsEditing(false)}
            />
        );
    }

    return (
        <>
            <div id={`club-${club.id}`} className="bg-white p-4 rounded-lg shadow-sm border border-orange-100 transition-all hover:shadow-md mb-3 relative overflow-hidden">
                {/* Visual indicator for "Club" */}
                <div className="absolute top-0 right-0 flex flex-col items-end">
                    <div className="bg-orange-100 text-orange-600 text-xs font-bold px-2 py-1 rounded-bl-lg">
                        CLUB
                    </div>
                    {club.status === 'pending' && (
                        <div className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-1 rounded-bl-lg mt-1 border-t border-yellow-200">
                            PENDING REVIEW
                        </div>
                    )}
                </div>

                <div className="flex gap-3 items-start">
                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 border border-gray-200 text-2xl">
                        🏰
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-800 text-lg leading-tight pr-8">{club.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                            📍 {club.address}
                        </p>
                    </div>
                </div>

                <div className="mt-3 bg-orange-50 p-2 rounded border border-orange-100">
                    {club.schedule ? (
                        <div className="mb-2">
                            {club.schedule.map((slot, idx) => {
                                const startTime = new Date(`1970-01-01T${slot.time}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                                const endTime = slot.endTime ? new Date(`1970-01-01T${slot.endTime}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;

                                return (
                                    <p key={idx} className="text-sm text-gray-800 font-medium">
                                        🗓️ {slot.day}s @ {startTime}{endTime ? ` - ${endTime}` : ''}
                                    </p>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-800 font-medium mb-1">
                            🗓️ {club.meetingDay}s @ {new Date(`1970-01-01T${club.meetingTime}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </p>
                    )}
                    {club.description && <p className="text-xs text-gray-600 italic mb-2">{club.description}</p>}

                    {/* Upcoming Events List */}
                    {events.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-orange-200">
                            <p className="text-xs font-bold text-orange-800 uppercase tracking-wide mb-1">Upcoming Events</p>
                            <ul className="space-y-1">
                                {events.map(event => (
                                    <li
                                        key={event.id}
                                        className="text-xs text-gray-800 flex justify-between items-center cursor-pointer hover:bg-orange-100 p-1 rounded transition-colors"
                                        onClick={() => setSelectedEvent(event)}
                                    >
                                        <span>
                                            <span className="font-semibold">
                                                {event.startTime?.toDate().toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                            </span>
                                            : {event.name}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-blue-600 font-medium bg-blue-50 px-1.5 py-0.5 rounded">
                                                👥 {(event.participants || []).length}
                                            </span>
                                            <span className="text-gray-400 text-[10px]">ℹ️</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Owner Actions */}
                <div className="mt-3 flex gap-2 flex-wrap">
                    {user?.isAdmin && club.status === 'pending' && (
                        <button
                            onClick={handleApprove}
                            className="flex-1 text-xs text-green-700 bg-green-100 border border-green-300 rounded py-1 hover:bg-green-200 font-bold"
                        >
                            ✅ Approve Club
                        </button>
                    )}
                    {isOwner && !showEventForm && (
                        <>
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex-1 text-xs text-indigo-600 border border-indigo-200 rounded py-1 hover:bg-indigo-50"
                            >
                                ✎ Edit Club
                            </button>
                            <button
                                onClick={handleDelete}
                                className="flex-1 text-xs text-red-600 border border-red-200 rounded py-1 hover:bg-red-50"
                            >
                                🗑️ Delete
                            </button>
                            <button
                                onClick={() => setShowEventForm(true)}
                                className="flex-1 text-xs text-green-600 border border-green-200 rounded py-1 hover:bg-green-50"
                            >
                                + Event
                            </button>
                        </>
                    )}
                </div>

                {showEventForm && (
                    <EventForm
                        clubId={club.id}
                        onCancel={() => setShowEventForm(false)}
                        onSuccess={() => setShowEventForm(false)}
                    />
                )}

                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                    <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(club.address || '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-sm font-medium hover:bg-gray-50 transition-colors text-center no-underline flex justify-center items-center gap-1"
                    >
                        📍 Navigate
                    </a>

                    <button
                        onClick={() => onViewOnMap(club)}
                        className="flex-1 bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                        View Map
                    </button>

                    <button
                        onClick={() => onOpenChat(club)}
                        className="flex-1 bg-indigo-50 border border-indigo-200 text-indigo-600 px-3 py-1.5 rounded text-sm font-medium hover:bg-indigo-100 transition-colors"
                    >
                        Chat
                    </button>

                    {club.website && (
                        <a
                            href={club.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 bg-gray-50 text-gray-600 border border-gray-200 px-3 py-1.5 rounded text-sm font-medium hover:bg-gray-100 transition-colors text-center no-underline"
                        >
                            Website
                        </a>
                    )}
                </div>
            </div>

            {/* Event Details Modal */}
            {selectedEvent && (
                <div className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
                        <div className="bg-orange-50 p-4 border-b border-orange-100 flex justify-between items-start">
                            <h3 className="font-bold text-lg text-gray-800">{selectedEvent.name}</h3>
                            <div className="flex gap-2">
                                {(isOwner || user?.isAdmin) && (
                                    <button
                                        onClick={async () => {
                                            if (window.confirm(`Delete event "${selectedEvent.name}"?`)) {
                                                try {
                                                    await deleteDoc(doc(db, 'events', selectedEvent.id));
                                                    toast.success("Event deleted");
                                                    // Remove from local state
                                                    setEvents(prev => prev.filter(e => e.id !== selectedEvent.id));
                                                    setSelectedEvent(null);
                                                } catch (err) {
                                                    console.error("Error deleting event:", err);
                                                    toast.error("Failed to delete event");
                                                }
                                            }
                                        }}
                                        className="text-red-400 hover:text-red-600 bg-red-50 hover:bg-red-100 p-1 rounded"
                                        title="Delete Event"
                                    >
                                        🗑️
                                    </button>
                                )}
                                <button
                                    onClick={() => setSelectedEvent(null)}
                                    className="text-gray-400 hover:text-gray-600 p-1"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                        <div className="p-4 space-y-3">
                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <span>🗓️</span>
                                <span className="font-medium">
                                    {selectedEvent.startTime?.toDate().toLocaleDateString([], { weekday: 'short', month: 'long', day: 'numeric' })}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <span>⏰</span>
                                <span className="font-medium">
                                    {selectedEvent.startTime?.toDate().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                </span>
                            </div>

                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <span>👥</span>
                                <span className="font-medium">
                                    {(selectedEvent.participants || []).length} Attending
                                </span>
                            </div>

                            {selectedEvent.description && (
                                <div className="bg-gray-50 p-3 rounded text-sm text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                                    {selectedEvent.description}
                                </div>
                            )}

                            <div className="flex gap-2 pt-2 flex-col sm:flex-row">
                                {user ? (
                                    <button
                                        onClick={async () => {
                                            if (!user) return;
                                            const isJoined = (selectedEvent.participants || []).includes(user.uid);
                                            try {
                                                const eventRef = doc(db, 'events', selectedEvent.id);
                                                if (isJoined) {
                                                    await updateDoc(eventRef, {
                                                        participants: arrayRemove(user.uid)
                                                    });
                                                    toast.success("Left event");
                                                    // Optimistic update
                                                    setSelectedEvent(prev => ({
                                                        ...prev,
                                                        participants: prev.participants.filter(id => id !== user.uid)
                                                    }));
                                                    setEvents(prev => prev.map(ev =>
                                                        ev.id === selectedEvent.id
                                                            ? { ...ev, participants: (ev.participants || []).filter(id => id !== user.uid) }
                                                            : ev
                                                    ));
                                                } else {
                                                    await updateDoc(eventRef, {
                                                        participants: arrayUnion(user.uid)
                                                    });
                                                    toast.success("Joined event!");
                                                    // Optimistic update
                                                    setSelectedEvent(prev => ({
                                                        ...prev,
                                                        participants: [...(prev.participants || []), user.uid]
                                                    }));
                                                    setEvents(prev => prev.map(ev =>
                                                        ev.id === selectedEvent.id
                                                            ? { ...ev, participants: [...(ev.participants || []), user.uid] }
                                                            : ev
                                                    ));
                                                }
                                            } catch (error) {
                                                console.error("Error joining/leaving event:", error);
                                                toast.error("Failed to update attendance");
                                            }
                                        }}
                                        className={`flex-1 py-2 rounded font-medium transition-colors ${(selectedEvent.participants || []).includes(user.uid)
                                            ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                            : 'bg-green-600 text-white hover:bg-green-700'
                                            }`}
                                    >
                                        {(selectedEvent.participants || []).includes(user.uid) ? '❌ Leave Event' : '✅ Join'}
                                    </button>
                                ) : (
                                    <button disabled className="flex-1 bg-gray-100 text-gray-400 py-2 rounded font-medium cursor-not-allowed">
                                        Login to Join
                                    </button>
                                )}

                                <button
                                    onClick={() => {
                                        setSelectedEvent(null);
                                        onOpenChat(selectedEvent);
                                    }}
                                    className="flex-1 bg-indigo-600 text-white py-2 rounded font-medium hover:bg-indigo-700 transition-colors"
                                >
                                    💬 Chat
                                </button>
                            </div>
                            {selectedEvent.link && (
                                <a
                                    href={selectedEvent.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full bg-gray-100 text-gray-700 py-2 rounded font-medium hover:bg-gray-200 transition-colors text-center no-underline mt-2"
                                >
                                    Registration Link ↗
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ClubCard;
